import { zUsdCents } from "@redotech/money/usd-amounts";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { ConversionFeature } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { rescueModalUiButtonSubtextSchema } from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const getUsageBillingSettings = z.object({
  kind: z.literal("usage"),
  enabled: z.boolean(),
  startDate: z.date().optional(),
  pricePerOrderInCents: z.number(),
  // Exists with date if renewOnPeriodEnd is false
  usageBillingEndDate: z.date().optional(),
  billingAccepted: z.boolean(),
  billIntentActiveOrders: z.boolean().optional(),
  billIntentAttributedOrdersPercent: z.number().gt(0).lte(100).optional(),
});

const getSubscriptionBillingSettings = z.object({
  kind: z.literal("subscription"),
  enabled: z.boolean(),
  startDate: z.date().optional(),
  periodFeeInCents: zUsdCents,
  pricePerOrderInCents: zUsdCents.optional(),
  // Exists with date if renewOnPeriodEnd is false
  billingAccepted: z.boolean(),
  billIntentActiveOrders: z.boolean().optional(),
  billIntentAttributedOrdersPercent: z.number().gt(0).lte(100).optional(),
});

const getDimensionalPackagingBillingSettings = z.object({
  enabled: z.boolean(),
  renewOnPeriodEnd: z.boolean(),
  startDate: z.date().optional(),
  period: z.literal(BillingPeriod.MONTHLY),
  periodFeeInCents: zUsdCents,
});

export const getConversionSettings = {
  name: "getConversionSettings" as const,
  input: z.object({ teamId: z.string() }),
  output: z.object({
    checkoutOptimizationEnabled: z.boolean(),
    billingAccepted: z.boolean(),
    checkoutOptimizationBilling: z
      .union([getUsageBillingSettings, getSubscriptionBillingSettings])
      .optional(),
    dimensionalPackagingBilling:
      getDimensionalPackagingBillingSettings.optional(),
    predictiveConversion: z
      .object({
        enabled: z.boolean(),
        intentRequested: z.boolean(),
        rescueModalAdminSettings: z.object({
          uiButtonSubtext: rescueModalUiButtonSubtextSchema,
        }),
      })
      .optional(),
    postPurchaseUpsell: z
      .object({ createOrderOnUpsell: z.boolean() })
      .optional(),
    ensureDiscountsCombineWithShippingDiscounts: z.boolean().optional(),
    syncDeliveryProfileProductMetafields: z.boolean().optional(),
    lockedFeatures: z.array(
      createEnum(ConversionFeature, { enumName: "ConversionFeature" }),
    ),
  }),
};
